// MIDAS V3 — loop_progress frame classifier (SP-2, PLUMBING)
//
// Part of the v3 segment-streaming FE (V3_SEGMENT_STREAMING_FE_SPEC.md §SP-2 +
// V3_FE_SEGMENT_STREAMING_CONTRACT.md §10.1.3). A v3 turn may emit
// `event: loop_progress` frames describing agentic-diagnosis tool work. This
// PURE, DEFENSIVE classifier normalizes a frame (of unpinned exact shape) into a
// render action the chatBotSlice reducer applies to the per-thread buffer. Pure
// so it is unit-testable in isolation and importable from the shared slice
// (mirrors the existing `ccSurface` static import into chatBotSlice.ts).
//
// ⚠ OPEN [BOT-SIDE] PIN: the exact v2 `loop_progress` JSON (discriminator field
// + full key sets) is not yet pinned in API_CONTRACTS.md. The FE contract
// §10.1.3 NAMES the three v2 shapes (`stage` / `step_end` / `finalizing`) and
// their key fields (`live_label` / `label` / `reason` / `checks`). We
// discriminate by FIELD PRESENCE (primary) with an explicit shape-hint field
// (`type` / `kind` / `phase`) as an override, and TOLERATE any unknown/missing
// field — never throw. Assumed field names (confirm with the bot team):
//   • stage      → `live_label` (display text)
//   • step_end   → `label`
//   • finalizing → `reason` (+ optional `label`)
//   • v1 shape   → `{ stage, round, rounds_max }` (currently the ONLY committed
//                  shape per API_CONTRACTS.md; `stage` is a CLOSED enum NAME, not
//                  display text) → IGNORED for rendering (SP-2 contract-
//                  sanctioned choice), but still suppresses the status line.

/** Normalized render action derived from a loop_progress frame. */
export interface LoopProgressClassification {
  /** True for any frame we could make sense of (used only for diagnostics; the
   *  reducer suppresses the status line for ANY object frame regardless). */
  recognized: boolean;
  /** What the row layer should do with this frame. */
  type: "stage" | "step_end" | "finalizing" | "ignore";
  /** Display text — `stage` → live_label; `step_end`/`finalizing` → label. */
  label?: string;
  /** `finalizing` reason, when present. */
  reason?: string;
  /** Whether anything should render (finalizing `clean`/`asking` → false). */
  render: boolean;
}

const asStr = (v: unknown): string | undefined =>
  typeof v === "string" && v.trim().length > 0 ? v.trim() : undefined;

/**
 * Classify a raw loop_progress frame. Never throws on an unexpected shape.
 */
export const classifyLoopProgressFrame = (
  frame: unknown
): LoopProgressClassification => {
  const ignore: LoopProgressClassification = {
    recognized: false,
    type: "ignore",
    render: false,
  };
  if (!frame || typeof frame !== "object" || Array.isArray(frame)) return ignore;
  const f = frame as Record<string, unknown>;

  const shapeHint = asStr(f.type) ?? asStr(f.kind) ?? asStr(f.phase);
  const reason = asStr(f.reason);
  const liveLabel = asStr(f.live_label) ?? asStr(f.liveLabel);
  const label = asStr(f.label);

  // FINALIZING — `reason` present (or explicit hint). `clean` / `asking` render
  // nothing; other reasons render `label` as a settled gray line when present.
  if (reason || shapeHint === "finalizing") {
    const render = !(reason === "clean" || reason === "asking");
    return { recognized: true, type: "finalizing", label, reason, render };
  }

  // STAGE (v2 live row) — display text is `live_label`. Checked before step_end
  // so a stage frame is never mistaken for a settle.
  if (liveLabel || shapeHint === "stage") {
    return { recognized: true, type: "stage", label: liveLabel, render: true };
  }

  // STEP_END — settle the live row with `label`.
  if (shapeHint === "step_end" || label) {
    return { recognized: true, type: "step_end", label, render: true };
  }

  // v1 shape { stage, round, rounds_max } (or any unrecognized shape) → IGNORE
  // for rendering. `recognized` is true for the known v1 shape so callers can
  // distinguish it from garbage; either way the reducer suppresses the status
  // line (any loop_progress frame supersedes it).
  const looksV1 =
    "rounds_max" in f || "round" in f || asStr(f.stage) !== undefined;
  return { recognized: looksV1, type: "ignore", render: false };
};
